import type {
  FrameGeometry,
  FrameAesthetics,
  FrameLogoFinish,
  FrameGeometryRanges,
  NumericFrameGeometryKey,
  WheelSize,
} from '@/types'
import { DEFAULT_FINISH_SELECTIONS } from '@/data/finishes'

export interface AestheticsOption {
  id:      string
  label:   string
  colour?: string
  logoFinish?: FrameLogoFinish
}

export const TITANIUM_FINISHES: AestheticsOption[] = [
  { id: 'raw-brushed',     label: 'Raw Brushed',     colour: '#9E9E96' },
  { id: 'bead-blasted',    label: 'Bead-blasted',    colour: '#BBBBB5' },
  { id: 'anodised-black',  label: 'Anodised Black',  colour: '#141414' },
  { id: 'anodised-blue',   label: 'Anodised Blue',   colour: '#1A3A6A' },
  { id: 'anodised-copper', label: 'Anodised Copper', colour: '#8B4F2A' },
  { id: 'polished',        label: 'Polished',        colour: '#E8E8E4' },
]

export const CARBON_WEAVES: AestheticsOption[] = [
  { id: 'standard-weave', label: 'Standard Weave' },
  { id: 'forged-carbon',  label: 'Forged Carbon' },
  { id: 'ud-carbon',      label: 'UD Carbon' },
  { id: 'hex-weave',      label: 'Hexagonal Weave' },
]

export const RESIN_COLOURS: AestheticsOption[] = [
  { id: 'clear',        label: 'Clear',        colour: '#E8E8E4' },
  { id: 'smoke',        label: 'Smoke',        colour: '#383838' },
]

export const OPTIONAL_ART: AestheticsOption[] = [
  { id: 'none',      label: 'None' },
  { id: 'geometric', label: 'Geometric' },
  { id: 'custom',    label: 'Custom Artwork' },
  { id: 'serial',    label: 'Serial Engraving' },
]

export const LOGO_COLOURS: AestheticsOption[] = [
  { id: 'gold',   label: 'Gold',   colour: '#C9A84C' },
  { id: 'polished-gold', label: 'Polished Gold', colour: '#FFD66B', logoFinish: 'polished-gold' },
  { id: 'silver', label: 'Silver', colour: '#C0C0C0' },
  { id: 'polished-silver', label: 'Polished Silver', colour: '#F2F4F7', logoFinish: 'polished-silver' },
  { id: 'black',  label: 'Black',  colour: '#141414' },
  { id: 'white',  label: 'White',  colour: '#F5F5F5' },
]

export const HEAD_BADGES: AestheticsOption[] = [
  { id: 'standard-shield', label: 'Standard Shield' },
  { id: 'minimal-text',    label: 'Minimal Text' },
  { id: 'none',            label: 'None' },
]

export interface GeometryField {
  key:   NumericFrameGeometryKey
  label: string
  unit:  'mm' | 'deg'
  min:   number
  max:   number
  step:  number
}

export const GEOMETRY_FIELDS: GeometryField[] = [
  { key: 'reachMm',            label: 'Reach',            unit: 'mm',  min: 400, max: 540, step: 1 },
  { key: 'headAngleDeg',       label: 'Head Angle',       unit: 'deg', min: 58,  max: 70,  step: 0.5 },
  { key: 'seatAngleDeg',       label: 'Eff. Seat Angle',  unit: 'deg', min: 70,  max: 82,  step: 0.5 },
  { key: 'seatAngleActualDeg', label: 'Act. Seat Angle',  unit: 'deg', min: 60,  max: 82,  step: 0.5 },
  { key: 'chainstayMm',        label: 'Chainstay',        unit: 'mm',  min: 410, max: 470, step: 1 },
  { key: 'bbDropMm',           label: 'BB Drop',          unit: 'mm',  min: -10, max: 80,  step: 1 },
  { key: 'headTubeMm',         label: 'Head Tube',        unit: 'mm',  min: 80,  max: 160, step: 1 },
  { key: 'forkLengthMm',       label: 'Fork Length',      unit: 'mm',  min: 400, max: 660, step: 1 },
  { key: 'forkOffsetMm',       label: 'Fork Offset',      unit: 'mm',  min: 30,  max: 60,  step: 1 },
  { key: 'saddleHeightMm',     label: 'Saddle Height',    unit: 'mm',  min: 550, max: 900, step: 1 },
]

/**
 * Fit & cockpit dimensions used to make the side-view render physically exact:
 * spacer stack, stem and crank length. Edited on the CMS Geometry tab; the
 * axles stay fixed while these reshape everything else.
 */
export const FIT_FIELDS: GeometryField[] = [
  { key: 'spacerStackMm',  label: 'Spacer Stack',  unit: 'mm',  min: 0,   max: 80,  step: 1 },
  { key: 'stemLengthMm',   label: 'Stem Length',   unit: 'mm',  min: 25,  max: 100, step: 1 },
  { key: 'stemAngleDeg',   label: 'Stem Angle',    unit: 'deg', min: -30, max: 30,  step: 1 },
  { key: 'crankLengthMm',  label: 'Crank Length',  unit: 'mm',  min: 150, max: 180, step: 2.5 },
]

export const ALL_GEOMETRY_RANGE_FIELDS: GeometryField[] = [...GEOMETRY_FIELDS, ...FIT_FIELDS]

export function defaultGeometryRanges(): FrameGeometryRanges {
  return Object.fromEntries(
    ALL_GEOMETRY_RANGE_FIELDS.map((field) => [field.key, { min: field.min, max: field.max }]),
  ) as FrameGeometryRanges
}

export function resolveGeometryField(field: GeometryField, ranges?: FrameGeometryRanges | null): GeometryField {
  const range = ranges?.[field.key]
  return {
    ...field,
    min: typeof range?.min === 'number' ? range.min : field.min,
    max: typeof range?.max === 'number' ? range.max : field.max,
  }
}

export function resolveGeometryFields(fields: GeometryField[], ranges?: FrameGeometryRanges | null): GeometryField[] {
  return fields.map((field) => resolveGeometryField(field, ranges))
}

export const WHEEL_SIZE_OPTIONS: { id: WheelSize; label: string }[] = [
  { id: '32',   label: '32"' },
  { id: '29',   label: '29"' },
  { id: '27.5', label: '27.5"' },
]

export const FORK_TRAVEL_OPTIONS: number[] = [120, 130, 140, 150, 160, 170, 180, 190, 200]

export interface FrameDefinition {
  id:          string
  name:        string
  material:    'titanium' | 'carbon'
  tagline:     string
  description: string
  specs:       string[]
  imageUrl?:   string
  /** Frame-only commission base price in NZD (frameset, before components). */
  basePrice:   number
  /** Frame-only weight in grams (Raider includes motor and battery). */
  baseWeightG: number
  geometryDefaults: FrameGeometry
  aestheticsDefaults: FrameAesthetics
}

export const FRAMES: FrameDefinition[] = [
  {
    id:          'felon-i',
    name:        'Felon I',
    material:    'carbon',
    tagline:     'Coming soon. Downhill race platform built for bike-park speed and full-send stability.',
    description: 'Coming soon. The Felon I is the Burton downhill line: long-travel, race-focused, and tuned for high-speed composure on steep terrain.',
    specs:       ['Downhill', '200mm Travel', 'Carbon Chassis', 'ML-Tuned Suspension'],
    basePrice:   7500,
    baseWeightG: 3600,
    imageUrl:    '/images/bike-lines/hidden-felon.png',
    geometryDefaults: {
      reachMm: 485, headAngleDeg: 62.5, seatAngleDeg: 76.0, seatAngleActualDeg: 71.0,
      chainstayMm: 450, bbDropMm: 8, headTubeMm: 115,
      frontWheel: '29', rearWheel: '29', forkTravelMm: 200, forkLengthMm: 620,
      forkOffsetMm: 46, spacerStackMm: 10, stemLengthMm: 45, stemAngleDeg: 0,
      saddleHeightMm: 720, crankLengthMm: 165,
    },
    aestheticsDefaults: {
      finish: '', carbonWeave: 'tubes-gloss-3k-twill', resinColour: 'clear',
      optionalArt: 'none', logoColour: 'gold', headBadge: 'standard-shield',
      tubeColour: 'carbon-black', lugColour: 'gold',
      zoneFinishes: { ...DEFAULT_FINISH_SELECTIONS },
      customText: '',
    },
  },
  {
    id:          'raptor-i',
    name:        'Raptor I',
    material:    'titanium',
    tagline:     'Enduro platform. Grade 9 Ti main triangle, carbon rear, machine-learned suspension.',
    description: 'The Raptor I is the current Burton enduro bike shown across the website. It pairs a triple-butted Grade 9 titanium main triangle with a carbon rear triangle, with suspension kinematics optimised by machine learning around real stage data.',
    specs:       ['Enduro', 'Grade 9 Titanium', 'Carbon Rear Triangle', 'ML-Tuned Suspension'],
    basePrice:   8900,
    baseWeightG: 3400,
    imageUrl:    '/images/burton-tuatara-rs0002.jpg',
    geometryDefaults: {
      reachMm: 470, headAngleDeg: 63.5, seatAngleDeg: 77.0, seatAngleActualDeg: 72.0,
      chainstayMm: 440, bbDropMm: 18, headTubeMm: 120,
      frontWheel: '29', rearWheel: '29', forkTravelMm: 160, forkLengthMm: 580,
      forkOffsetMm: 44, spacerStackMm: 20, stemLengthMm: 45, stemAngleDeg: 0,
      saddleHeightMm: 760, crankLengthMm: 170,
    },
    aestheticsDefaults: {
      finish: 'raw-brushed', carbonWeave: 'tubes-gloss-3k-twill', resinColour: 'clear',
      optionalArt: 'none', logoColour: 'gold', headBadge: 'standard-shield',
      tubeColour: 'raw-titanium', lugColour: 'carbon-black',
      zoneFinishes: { ...DEFAULT_FINISH_SELECTIONS },
      customText: '',
    },
  },
  {
    id:          'typhoon-i',
    name:        'Typhoon I',
    material:    'carbon',
    tagline:     'Coming soon. Down-country platform, fast, efficient, and ready for rough technical days.',
    description: 'Coming soon. The Typhoon I is the Burton down-country line: lighter, sharper, and more efficient than the enduro bikes while still built around real trail control.',
    specs:       ['Down-Country', 'Lightweight Carbon', '120-140mm Travel', 'ML-Tuned Suspension'],
    basePrice:   6900,
    baseWeightG: 2400,
    imageUrl:    '/images/bike-lines/hidden-typhoon.png',
    geometryDefaults: {
      reachMm: 455, headAngleDeg: 65.5, seatAngleDeg: 76.5, seatAngleActualDeg: 71.5,
      chainstayMm: 435, bbDropMm: 38, headTubeMm: 100,
      frontWheel: '29', rearWheel: '29', forkTravelMm: 130, forkLengthMm: 550,
      forkOffsetMm: 44, spacerStackMm: 25, stemLengthMm: 50, stemAngleDeg: 0,
      saddleHeightMm: 760, crankLengthMm: 170,
    },
    aestheticsDefaults: {
      finish: '', carbonWeave: 'tubes-gloss-3k-twill', resinColour: 'clear',
      optionalArt: 'none', logoColour: 'gold', headBadge: 'standard-shield',
      tubeColour: 'carbon-black', lugColour: 'gold',
      zoneFinishes: { ...DEFAULT_FINISH_SELECTIONS },
      customText: '',
    },
  },
  {
    id:          'raider',
    name:        'Raider',
    material:    'carbon',
    tagline:     'Coming soon. E-Enduro platform with Burton Kinetics suspension tuned for the mass it carries.',
    description: 'Coming soon. The Raider is the Burton E-Enduro line: the same custom-fit thinking as the Raptor I, with motor-assisted range for bigger days and repeated descents.',
    specs:       ['E-Enduro', 'Carbon Chassis', '170mm Travel', 'ML-Tuned Suspension'],
    basePrice:   9500,
    baseWeightG: 8200,
    imageUrl:    '/images/bike-lines/hidden-raider.png',
    geometryDefaults: {
      reachMm: 475, headAngleDeg: 63.5, seatAngleDeg: 77.5, seatAngleActualDeg: 72.5,
      chainstayMm: 446, bbDropMm: 20, headTubeMm: 125,
      frontWheel: '29', rearWheel: '27.5', forkTravelMm: 170, forkLengthMm: 590,
      forkOffsetMm: 44, spacerStackMm: 20, stemLengthMm: 45, stemAngleDeg: 0,
      saddleHeightMm: 765, crankLengthMm: 165,
    },
    aestheticsDefaults: {
      finish: '', carbonWeave: 'tubes-gloss-3k-twill', resinColour: 'clear',
      optionalArt: 'none', logoColour: 'gold', headBadge: 'standard-shield',
      tubeColour: 'carbon-black', lugColour: 'gold',
      zoneFinishes: { ...DEFAULT_FINISH_SELECTIONS },
      customText: '',
    },
  },
]
